import { mslaGetToken } from '@shape/util-ui';
import { jwtDecode } from 'jwt-decode';
import axios, { AxiosError, AxiosInstance, AxiosRequestConfig, AxiosRequestHeaders } from 'axios';
import posthog from 'posthog-js';
import { UseMutationOptions, UseQueryOptions } from '@tanstack/react-query';
import { t } from 'i18next';
import { dayjs } from 'lib';
import { User } from 'types';
import { ADD_AUTH_TOKEN, ADD_MESSAGE } from '../appContext';
import * as constants from '../config/constants';
import { MSAL_CONFIG } from './auth';

export type GenericQueryOptions<TData> = Omit<
  UseQueryOptions<TData, AxiosError, TData>,
  'queryKey' | 'queryFn'
>;

export type GenericMutationOptions<TData, TPayload = null> = Omit<
  UseMutationOptions<TData, TPayload, AxiosError>,
  'mutationKey' | 'mutationFn'
>;
export const showErrorMesageByCode = (errorCode: number) => {
  if ([500, 503].indexOf(errorCode) > -1) {
    const event = new CustomEvent(ADD_MESSAGE, {
      detail: {
        type: 'error',
        title: t('messages:fetch_error.title'),
        message: 'Internal Server Error',
      }, // todo add i18n
    });
    window.dispatchEvent(event);
  }
  if (errorCode === 403) {
    const event = new CustomEvent(ADD_MESSAGE, {
      detail: {
        type: 'error',
        title: 'Unauthorized',
        message: "You don't have permission for this action",
      },
    });
    window.dispatchEvent(event);
  }
  if (errorCode === 504) {
    const event = new CustomEvent(ADD_MESSAGE, {
      detail: {
        type: 'error',
        title: 'Ops',
        message: 'Your request took longer than expected. Check for updates in 5 min.',
      },
    });
    window.dispatchEvent(event);
  }
};

const axiosAPI: AxiosInstance = axios.create();

const getToken = () => {
  const email = localStorage.getItem(constants.B2C_LOCAL_STORAGE_EMAIL);
  const domainHint = localStorage.getItem(constants.B2C_LOCAL_STORAGE_DOMAIN_HINT) ?? undefined;

  if (email) {
    return new Promise((resolve) => {
      mslaGetToken(MSAL_CONFIG(email, domainHint), (token: string) => {
        resolve(token);
      });
    });
  }

  return;
};

axiosAPI.interceptors.response.use(
  async (response) => {
    return response;
  },
  async (error) => {
    const originalRequest = error.config;
    showErrorMesageByCode(error.response?.status);
    const properties = {
      message: error.message || 'Unknown error',
      name: error.name || 'AxiosError',
      stack: error.stack || 'No stack trace',
      code: error.code ?? null,
      status: error.response?.status ?? null,
      statusText: error.response?.statusText ?? null,
      url: error.config?.url || 'Unknown URL',
      method: error.config?.method || 'Unknown Method',
      app_version: __APP_VERSION__,
    };
    posthog.capture('API_ERROR', properties);
    if (error.response?.status === 401 && !originalRequest._retry) {
      originalRequest._retry = true;
      const webToken = await getToken();
      originalRequest.headers['Authorization'] = `Bearer ${webToken}`;
      const event = new CustomEvent(ADD_AUTH_TOKEN, { detail: webToken });
      window.dispatchEvent(event);
      return axios(originalRequest);
    }
    return Promise.reject(error);
  },
);

axiosAPI.interceptors.request.use(
  async (config) => {
    const token = (config.headers?.['Authorization'] ??
      axiosAPI.defaults.headers.common['Authorization']) as string;

    if (!config.headers?.['Authorization'] && !constants.IS_DEMO) {
      const webToken = await getToken();

      config.headers['Authorization'] = `Bearer ${webToken}`;
      return Promise.resolve(config);
    }

    const decoded: User | undefined = token ? jwtDecode(token.replace('Bearer', '')) : undefined;
    if (decoded?.exp) {
      const expiration = new Date(decoded.exp * 1000);
      const invalidated = dayjs(expiration).isBefore(dayjs().add(10, 'minutes'));
      if (invalidated) {
        const webToken = await getToken();
        const event = new CustomEvent(ADD_AUTH_TOKEN, { detail: webToken });
        window.dispatchEvent(event);
        if (config.headers?.['Authorization']) {
          config.headers['Authorization'] = `Bearer ${webToken}`;
        }
      }
    }

    return Promise.resolve(config);
  },
  (error) => {
    return Promise.reject(error);
  },
);

interface IRequestProps<Params, Payload> {
  params?: Params;
  headers?: AxiosRequestHeaders;
  payload?: Payload;
  requestConfig?: AxiosRequestConfig<Record<string, unknown>>;
}

export const get =
  <Response, Params = null>(
    url: string,
    params?: Params,
    headers?: AxiosRequestHeaders,
    requestConfig?: AxiosRequestConfig<Record<string, unknown>>,
  ) =>
  async () => {
    const { data } = await axiosAPI.get<Response>(url, { params, headers, ...requestConfig });
    return data;
  };

export const post = <Payload, Response = null, Params = null>(
  url: string,
  { payload, params, headers, requestConfig }: IRequestProps<Params, Payload>,
) => axiosAPI.post<Response>(url, payload, { params, headers, ...requestConfig });

export const put = <Payload, Response = null, Params = null>(
  url: string,
  { payload, params, headers }: IRequestProps<Params, Payload>,
): Promise<Response> => {
  return axiosAPI.put(url, payload, { params, headers });
};

export const patch = <Payload, Response = null, Params = null>(
  url: string,
  { payload, params, headers }: IRequestProps<Params, Payload>,
): Promise<Response> => {
  return axiosAPI.patch(url, payload, { params, headers });
};

export const del = <TPayload, TResponse = null, TParams = null>(
  url: string,
  { payload, params, headers }: IRequestProps<TParams, TPayload>,
): Promise<TResponse> => axiosAPI.delete(url, { data: payload, params, headers });

export default axiosAPI;
